import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useIaJobs } from '../context/IaJobsProvider';
import { useOnboardingPack } from '../context/OnboardingPackProvider';
import { iaApi } from '../services/api';
import { humanizeJobError } from '../utils/humanizeJobError';
import { getOnboardingSuggestionsFromJobResult } from '../utils/iaJobResult';
import { syncProfileBeforeOnboarding } from '../utils/syncProfileBeforeOnboarding';
import { buildWizardFormFromUser } from '../utils/wizardFormFromUser';
import {
  readWizardDraft,
  writeWizardDraft,
  clearWizardDraft,
  isWizardDraftResumable,
} from '../utils/wizardDraftStorage';
import {
  WIZARD_STEPS,
  WIZARD_STEP_ORDER,
  validateWizardStep,
  canEnqueueWizardJob,
  parseWorkoutsPerWeek,
  MAX_REGENERATION_ROUNDS,
} from '../constants/workoutWizardSteps';

const ONBOARDING_JOB_TIMEOUT_MS = 130000;
const VARIETY_REGENERATE_MSG =
  'Proponha divisão e exercícios claramente diferentes do pack anterior. Mantenha coerência com o perfil.';

function stepIndex(stepId) {
  return WIZARD_STEP_ORDER.indexOf(stepId);
}

function nextFormStep(stepId) {
  const idx = stepIndex(stepId);
  if (idx < 0 || idx >= WIZARD_STEP_ORDER.length - 1) return null;
  return WIZARD_STEP_ORDER[idx + 1];
}

function prevFormStep(stepId) {
  const idx = stepIndex(stepId);
  if (idx <= 0) return null;
  const prev = WIZARD_STEP_ORDER[idx - 1];
  if (prev === WIZARD_STEPS.PIPELINE) return WIZARD_STEPS.LIMITATIONS;
  return prev;
}

export function useWorkoutWizard() {
  const navigate = useNavigate();
  const { user, updateUser, refreshUser } = useAuth();
  const { registerJobCallback, refreshJobs, setOnboardingReview } = useIaJobs();
  const {
    suggestions,
    perWorkoutNotes,
    desiredCount,
    setDesiredCount,
    globalNotes,
    setGlobalNotes,
    extraDetail,
    setExtraDetail,
    workoutComposition,
    setWorkoutComposition,
    compositionExplicit,
    engineLabel,
    draftSavedAt,
    setJobResult,
    prepareNewGeneration,
    removeSuggestion,
    updatePerWorkoutNote,
    clearDraft,
    applyAll,
    applyOne,
    wizardOpen,
    wizardMode,
    wizardInitialStep,
    wizardContextLabel,
    closeWizard,
    regenerationRound,
    incrementRegenerationRound,
    resetRegenerationRound,
  } = useOnboardingPack();

  const [currentStep, setCurrentStep] = useState(WIZARD_STEPS.PROFILE);
  const [formData, setFormData] = useState(() => buildWizardFormFromUser(user));
  const [stepError, setStepError] = useState('');
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelinePhase, setPipelinePhase] = useState('recebidos');
  const [pipelineErr, setPipelineErr] = useState('');
  const [jobId, setJobId] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [stepSaving, setStepSaving] = useState(false);
  const [engines, setEngines] = useState([]);

  const finishJobRef = useRef(null);
  const wizardInitRef = useRef(false);

  useEffect(() => {
    if (!wizardOpen) {
      wizardInitRef.current = false;
      return;
    }
    if (wizardInitRef.current) return;
    wizardInitRef.current = true;

    setFormData(buildWizardFormFromUser(user));
    const draft = user?.id ? readWizardDraft(user.id) : null;
    const initial =
      wizardInitialStep && wizardInitialStep !== WIZARD_STEPS.PROFILE
        ? wizardInitialStep
        : isWizardDraftResumable(draft)
          ? draft.currentStep
          : wizardInitialStep || WIZARD_STEPS.PROFILE;
    if (isWizardDraftResumable(draft) && draft.formData && initial !== WIZARD_STEPS.REVIEW) {
      setFormData({ ...buildWizardFormFromUser(user), ...draft.formData });
    }
    if (initial === WIZARD_STEPS.REVIEW && suggestions.length > 0) {
      setCurrentStep(WIZARD_STEPS.REVIEW);
    } else if (initial === WIZARD_STEPS.PIPELINE && !pipelineLoading) {
      setCurrentStep(WIZARD_STEPS.LIMITATIONS);
    } else {
      setCurrentStep(initial);
    }
    setStepError('');
    setPipelineErr('');
  }, [wizardOpen, wizardInitialStep, user, suggestions.length]);

  useEffect(() => {
    if (!wizardOpen || !user?.id) return undefined;
    const t = setTimeout(() => {
      if (currentStep === WIZARD_STEPS.REVIEW) return;
      writeWizardDraft(user.id, {
        currentStep,
        formData,
        wizardMode,
        contextLabel: wizardContextLabel,
        jobId: jobId || undefined,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    wizardOpen,
    user?.id,
    currentStep,
    formData,
    wizardMode,
    wizardContextLabel,
    jobId,
  ]);

  useEffect(() => {
    if (!user?.id) return;
    iaApi.getEngines().then((res) => setEngines(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, [user?.id]);

  const finishJob = useCallback(
    (resultOrJob, { failedMessage } = {}) => {
      setJobId(null);
      refreshJobs();
      if (failedMessage) {
        setPipelineErr(humanizeJobError(failedMessage));
        setPipelineLoading(false);
        setPipelinePhase('recebidos');
        setCurrentStep(WIZARD_STEPS.LIMITATIONS);
        return;
      }
      const arr = getOnboardingSuggestionsFromJobResult(resultOrJob);
      if (!arr.length) {
        setPipelineErr('A IA não retornou treinos válidos. Tente novamente.');
        setPipelineLoading(false);
        setPipelinePhase('recebidos');
        setCurrentStep(WIZARD_STEPS.LIMITATIONS);
        return;
      }
      setPipelineErr('');
      const eng =
        engines.find((e) => String(e.name || '').toLowerCase() === 'mistral') || engines[0];
      setJobResult(arr, { engine: eng?.name || 'IA' });
      setPipelinePhase('ok');
      setPipelineLoading(false);
      setOnboardingReview({ count: arr.length });
      setCurrentStep(WIZARD_STEPS.REVIEW);
    },
    [engines, refreshJobs, setJobResult, setOnboardingReview]
  );

  finishJobRef.current = finishJob;

  const registerJobWatch = useCallback(
    (id) => {
      setJobId(id);
      registerJobCallback(id, {
        onUpdate: (job) => setPipelinePhase(job.phase || 'recebidos'),
        onDone: (result) => finishJob(result),
        onFailed: (msg) =>
          finishJob(null, { failedMessage: msg || 'Não foi possível gerar treinos.' }),
      });
    },
    [finishJob, registerJobCallback]
  );

  useEffect(() => {
    if (!pipelineLoading) return undefined;
    const t = setTimeout(() => {
      finishJobRef.current(null, {
        failedMessage: 'A geração demorou demais. Tente novamente.',
      });
    }, ONBOARDING_JOB_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [pipelineLoading]);

  useEffect(() => {
    if (!pipelineLoading || !jobId) return undefined;
    let cancelled = false;
    const pollJob = async () => {
      try {
        const res = await iaApi.getIaJob(jobId);
        const job = res.data;
        if (cancelled || !job) return;
        setPipelinePhase(job.phase || 'recebidos');
        if (job.status === 'done') finishJobRef.current(job);
        else if (job.status === 'failed') {
          finishJobRef.current(null, {
            failedMessage: job.errorMessage || 'Não foi possível gerar treinos.',
          });
        }
      } catch {
        /* provider global */
      }
    };
    pollJob();
    const t = setInterval(pollJob, 1500);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [pipelineLoading, jobId]);

  const persistStep = useCallback(async () => {
    await syncProfileBeforeOnboarding({ formData, updateUser });
    const count = parseWorkoutsPerWeek(formData.workoutsPerWeek) || 3;
    setDesiredCount(String(count));
  }, [formData, updateUser, setDesiredCount]);

  const runGenerate = useCallback(
    async ({ isRegeneration = false } = {}) => {
      const count = parseWorkoutsPerWeek(formData.workoutsPerWeek) || parseInt(desiredCount, 10) || 3;
      if (!Number.isFinite(count) || count < 2 || count > 4) {
        setStepError('Informe entre 2 e 4 treinos.');
        return;
      }

      if (!canEnqueueWizardJob(formData)) {
        setStepError('Complete o perfil antes de gerar.');
        return;
      }

      if (isRegeneration && regenerationRound >= MAX_REGENERATION_ROUNDS) {
        setPipelineErr('Limite de regenerações atingido. Aprove o pack atual.');
        return;
      }

      if (!isRegeneration) {
        prepareNewGeneration();
        resetRegenerationRound();
      } else {
        incrementRegenerationRound();
      }

      setPipelineErr('');
      setStepError('');
      setPipelineLoading(true);
      setPipelinePhase('recebidos');
      setCurrentStep(WIZARD_STEPS.PIPELINE);

      try {
        await syncProfileBeforeOnboarding({ formData, updateUser });
        await refreshUser();
        setDesiredCount(String(count));

        const mergedInstructions = [
          globalNotes.trim(),
          ...perWorkoutNotes.map((n, i) => {
            const label = suggestions[i]?.name || `Treino ${i + 1}`;
            return n != null && String(n).trim() ? `${label}: ${String(n).trim()}` : '';
          }).filter(Boolean),
        ]
          .filter(Boolean)
          .join('\n');

        const res = await iaApi.createIaJob({
          type: 'onboarding_pack',
          contextLabel: wizardContextLabel || 'plan_wizard',
          payload: {
            instructions: mergedInstructions,
            previousTrainingBase: formData.previousTrainingBase || '',
            desiredWorkoutCount: count,
            extraDetail: extraDetail.trim(),
            workoutComposition,
            compositionExplicit,
            ...(isRegeneration || suggestions.length > 0
              ? { varietyInstructions: VARIETY_REGENERATE_MSG }
              : {}),
          },
        });

        const data = res.data || {};
        const newJobId = data.jobId || data.id;

        if (data.status === 'done') {
          finishJob(data);
          return;
        }

        if (!newJobId) throw new Error('Resposta inválida ao enfileirar geração.');

        registerJobWatch(newJobId);
        closeWizard();
      } catch (err) {
        if (err?.message && !err?.response) {
          setPipelineLoading(false);
          setPipelinePhase('recebidos');
          setPipelineErr(err.message);
          setCurrentStep(WIZARD_STEPS.LIMITATIONS);
          return;
        }
        if (err?.response?.status === 409) {
          const existing = err.response?.data?.job;
          const existingId = existing?.id || existing?.jobId;
          if (existingId) {
            if (existing.status === 'done') {
              finishJob(existing);
              return;
            }
            if (existing.status === 'failed') {
              finishJob(null, {
                failedMessage: existing.errorMessage || 'Job anterior falhou.',
              });
              return;
            }
            registerJobWatch(existingId);
            closeWizard();
            return;
          }
        }
        const msg = humanizeJobError(
          err?.response?.data?.error || err?.message || 'Não foi possível gerar treinos.'
        );
        finishJob(null, { failedMessage: msg });
      }
    },
    [
      formData,
      desiredCount,
      regenerationRound,
      prepareNewGeneration,
      resetRegenerationRound,
      incrementRegenerationRound,
      updateUser,
      refreshUser,
      setDesiredCount,
      globalNotes,
      perWorkoutNotes,
      suggestions,
      extraDetail,
      workoutComposition,
      compositionExplicit,
      wizardContextLabel,
      finishJob,
      registerJobWatch,
      closeWizard,
    ]
  );

  const goNext = useCallback(async () => {
    setStepError('');
    const errors = validateWizardStep(currentStep, formData);
    if (errors.length) {
      setStepError(errors.join(' '));
      return;
    }
    setStepSaving(true);
    try {
      await persistStep();
    } catch (e) {
      setStepError(e.message || 'Erro ao salvar perfil.');
      return;
    } finally {
      setStepSaving(false);
    }

    if (currentStep === WIZARD_STEPS.LIMITATIONS) {
      await runGenerate({ isRegeneration: false });
      return;
    }

    const next = nextFormStep(currentStep);
    if (next && next !== WIZARD_STEPS.PIPELINE) setCurrentStep(next);
  }, [currentStep, formData, persistStep, runGenerate]);

  const goBack = useCallback(() => {
    setStepError('');
    if (currentStep === WIZARD_STEPS.REVIEW) {
      setCurrentStep(WIZARD_STEPS.LIMITATIONS);
      return;
    }
    if (currentStep === WIZARD_STEPS.PIPELINE) {
      setCurrentStep(WIZARD_STEPS.LIMITATIONS);
      return;
    }
    const prev = prevFormStep(currentStep);
    if (prev) setCurrentStep(prev);
  }, [currentStep]);

  const cancelPipeline = useCallback(async () => {
    if (!jobId) {
      setPipelineLoading(false);
      setCurrentStep(WIZARD_STEPS.LIMITATIONS);
      return;
    }
    try {
      await iaApi.cancelIaJob(jobId);
      finishJob(null, { failedMessage: 'Geração cancelada.' });
    } catch {
      finishJob(null, { failedMessage: 'Não foi possível cancelar. Aguarde.' });
    }
  }, [jobId, finishJob]);

  const handleRegenerate = useCallback(() => {
    runGenerate({ isRegeneration: true });
  }, [runGenerate]);

  const canRegenerate = regenerationRound < MAX_REGENERATION_ROUNDS;

  const handleApplyAll = useCallback(async () => {
    setApplyLoading(true);
    setPipelineErr('');
    try {
      const result = await applyAll();
      if (user?.id) clearWizardDraft(user.id);
      closeWizard();
      navigate('/workouts-manage', {
        state: {
          wizardJustApplied: true,
          count: result.count,
          createdIds: result.createdIds || [],
        },
      });
    } catch (e) {
      setPipelineErr(e.message || 'Erro ao criar treinos.');
    } finally {
      setApplyLoading(false);
    }
  }, [applyAll, closeWizard, navigate, user?.id]);

  const handleClose = useCallback(() => {
    if (user?.id && currentStep !== WIZARD_STEPS.REVIEW) {
      writeWizardDraft(user.id, {
        currentStep: pipelineLoading ? WIZARD_STEPS.PIPELINE : currentStep,
        formData,
        wizardMode,
        contextLabel: wizardContextLabel,
        jobId: jobId || undefined,
      });
    }
    if (pipelineLoading) {
      const ok = window.confirm(
        'Progresso salvo. A geração continua na barra Motor IA no topo. Toque em Rever quando estiver pronto. Fechar agora?'
      );
      if (!ok) return;
    }
    closeWizard();
  }, [
    pipelineLoading,
    closeWizard,
    user?.id,
    currentStep,
    formData,
    wizardMode,
    wizardContextLabel,
    jobId,
  ]);

  return {
    wizardOpen,
    wizardMode,
    currentStep,
    formData,
    setFormData,
    stepError,
    pipelineLoading,
    pipelinePhase,
    pipelineErr,
    engineLabel,
    suggestions,
    perWorkoutNotes,
    globalNotes,
    setGlobalNotes,
    extraDetail,
    setExtraDetail,
    workoutComposition,
    setWorkoutComposition,
    draftSavedAt,
    applyLoading,
    regenerationRound,
    canRegenerate,
    goNext,
    goBack,
    handleClose,
    cancelPipeline,
    handleRegenerate,
    handleApplyAll,
    removeSuggestion,
    updatePerWorkoutNote,
    clearDraft,
    jobId,
    applyOne,
    stepSaving,
  };
}
